import logo from '../../../assets/PNG/transparentLogo.png';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import InvitationService from '../../../services/bioplus-services/invitation';
import { Button, Loader } from '../../../Components/uielements';
import { ErrorCross, ErrorExclamation, SuccessSvg } from '../../../Components/icons';
import { useUserContext } from '../../../hooks/UserContext';
import {
  LayoutV2,
  ContentWrapper,
  LogoContainer,
  HeadingTitle,
  MessageTitle,
  LoadingContainer,
  ErrorContainer,
  SuccessContainer,
  InfoText,
} from './userInvitation.style';
import {
  OutlineButton,
  ModalButtonWrapper,
} from '../../../services/settings/appStyle/global.style';
import FreeUserPopUpMessage from '../LoginV2/modals/freeUserPopUp';
import iosQR from '../../../assets/PNG/app_scan_qr.png';
import androidQR from '../../../assets/PNG/app_scan_qr.png';
import { useAuth } from '../../../hooks/AuthContext';

const RETURN_URL_KEY = 'agmanager_invitation_returnUrl';
const SESSION_KEY = 'session';

const isSessionValid = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return false;

    const session = JSON.parse(raw);

    if (!session?.token) return false;

    if (session?.expiry && Date.now() > session.expiry) return false;

    return true;
  } catch {
    return false;
  }
};

const UserInvitationV2 = () => {
  const [searchParams] = useSearchParams();
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const userContext = useUserContext();
  const { logout } = useAuth();

  // status: 'checking' | 'loading' | 'display' | 'accepting' | 'success' | 'error'
  const [status, setStatus] = useState('checking');
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showFreeUserPopUp, setShowFreeUserPopUp] = useState(false);

  const invitationCode = searchParams.get('code');

  const userRoles = userContext?.company;
  const isVisitor = userRoles?.role === 'VISITOR' && userRoles?.roleAccess === 'M';

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const loggedInViaSession = isSessionValid();
        const loggedInViaContext = !!(userContext?.user?.id || userContext?.user?._id);
        const isLoggedIn = loggedInViaSession || loggedInViaContext;

        if (!isLoggedIn) {
          const currentUrl = window.location.pathname + window.location.search;
          localStorage.setItem(RETURN_URL_KEY, currentUrl);
          navigate('/');
          return;
        }

        await fetchInvitationDetails();
      } catch (error) {
        console.error('Error checking login status:', error);
        setErrorMessage('Failed to verify login status');
        setStatus('error');
      }
    };

    checkLoginStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvitationDetails = async () => {
    try {
      setStatus('loading');
      setErrorMessage(null);

      if (!invitationCode) {
        setErrorMessage('Invalid invitation link — missing code parameter');
        setStatus('error');
        return;
      }

      const response = await InvitationService.getInvitationDetails({
        invitationCode: invitationCode,
      });

      if (response) {
        setInvitationDetails(response);
        setStatus('display');
      } else {
        setErrorMessage('This invitation is invalid or has expired');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error fetching invitation details:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to load invitation details');
      setStatus('error');
    }
  };

  const handleAcceptInvitation = async () => {
    try {
      setStatus('accepting');
      setErrorMessage(null);

      const response = await InvitationService.acceptInvitation({
        invitationCode: invitationCode,
      });

      if (response) {
        setStatus('success');

        // If VISITOR user, show FreeUserPopUp instead of redirecting
        if (isVisitor) {
          setTimeout(() => {
            setStatus('freePopUP');
          }, 2000);
        } else {
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to accept invitation');
      setStatus('error');
    }
  };

  const handleDecline = () => {
    if (isVisitor) {
      handleLogout();
    } else {
      navigate('/');
    }
  };

  const handleRetry = () => {
    fetchInvitationDetails();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ─── Render: Checking session ─────────────────────────────────────────────────
  if (status === 'checking') {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />
            <HeadingTitle>
              Friend Invitation <span>(Verification)</span>
            </HeadingTitle>
          </LogoContainer>
          <LoadingContainer>
            <Loader size="extraMedium" />
            <InfoText>Verifying your session...</InfoText>
          </LoadingContainer>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  // ─── Render: Loading invitation ───────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />
            <HeadingTitle>You're invited to join AGManager™</HeadingTitle>
          </LogoContainer>
          <LoadingContainer>
            <Loader size="extraMedium" />
            <InfoText>Loading invitation details...</InfoText>
          </LoadingContainer>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  // ─── Render: Display invitation ───────────────────────────────────────────────
  if (status === 'display' && invitationDetails) {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />
            <HeadingTitle>You're invited to join AGManager™</HeadingTitle>
          </LogoContainer>

          <MessageTitle>
            {invitationDetails?.invitor?.firstName} {invitationDetails?.invitor?.lastName} has
            invited you to collaborate on AGManager™.
          </MessageTitle>

          <InfoText style={{ marginTop: '8px', textAlign: 'center' }}>
            {invitationDetails?.message ||
              'Join AGManager™ by accepting this invitation and get started right away.'}
          </InfoText>

          <ModalButtonWrapper style={{ marginTop: '32px', marginBottom: '20px' }}>
            <OutlineButton onClick={handleDecline}>Not Now</OutlineButton>
            <Button varient="normal" onClick={handleAcceptInvitation}>
              Accept
            </Button>
          </ModalButtonWrapper>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  // ─── Render: Processing acceptance ───────────────────────────────────────────
  if (status === 'accepting') {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />

            <HeadingTitle>You're invited to join AGManager™</HeadingTitle>
          </LogoContainer>
          <LoadingContainer>
            <Loader size="extraMedium" />
            <InfoText>Accepting invitation...</InfoText>
          </LoadingContainer>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  // ─── Render: Success ──────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />

            <HeadingTitle>You're invited to join AGManager™</HeadingTitle>
          </LogoContainer>
          <SuccessContainer>
            <SuccessSvg style={{ width: '100px', height: '100px' }} />
            <MessageTitle>
              You have successfully accepted the invitation. You will now be able to collaborate
              with your friend on AGManager™.
            </MessageTitle>
            {!isVisitor && <InfoText>Redirecting to home...</InfoText>}
          </SuccessContainer>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  // ─── Render: Error ────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <LayoutV2>
        <ContentWrapper>
          <LogoContainer>
            <img src={logo} style={{ width: 300 }} alt="AGManager Logo" />

            <HeadingTitle>You're invited to join AGManager™</HeadingTitle>
          </LogoContainer>
          <ErrorContainer>
            {errorMessage === 'Invitation Already Accepted!' ||
            errorMessage === 'Invitation has already been accepted!' ? (
              <ErrorExclamation style={{ width: '100px', height: '100px' }} />
            ) : (
              <ErrorCross style={{ width: '100px', height: '100px' }} />
            )}
            <MessageTitle>
              {errorMessage || 'An error occurred while processing your invitation.'}
            </MessageTitle>
            <ModalButtonWrapper style={{ marginTop: '24px', marginBottom: '20px' }}>
              <OutlineButton onClick={handleDecline}>Go Home</OutlineButton>
              <Button onClick={handleRetry}>Try Again</Button>
            </ModalButtonWrapper>
          </ErrorContainer>
        </ContentWrapper>
      </LayoutV2>
    );
  }

  if (status === 'freePopUP') {
    return (
      <>
        <FreeUserPopUpMessage onClose={handleLogout} androidQR={androidQR} iosQR={iosQR} />
      </>
    );
  }

  return null;
};

export default UserInvitationV2;
